import { Component, ViewChild, TemplateRef, ChangeDetectorRef, inject, OnDestroy, output } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { NgbModal, NgbModalRef, NgbDropdownModule } from "@ng-bootstrap/ng-bootstrap";
import { StripeService } from "../../services/stripe.service";
import { AuthService } from "../../services/auth.service";
import posthog from 'posthog-js';
import { environment } from "../../../environments/environment";
import { AnimationOptions, LottieComponent } from "ngx-lottie";
import limited from "./limited.json";
import { PricingCardComponent, PricingOption, PricingPlan } from "./pricing-card.component";

@Component({
    selector: "app-founding-member-modal",
    imports: [CommonModule, FormsModule, LottieComponent, PricingCardComponent, NgbDropdownModule],
    templateUrl: "./founding-member-modal.component.html",
    styleUrl: "./founding-member-modal.component.scss",
    standalone: true
})
export class FoundingMemberModalComponent implements OnDestroy {
    private readonly _cdr = inject(ChangeDetectorRef);
    private readonly _modalService = inject(NgbModal);
    private readonly _stripeService = inject(StripeService);
    private readonly _authService = inject(AuthService);

    @ViewChild("foundingMemberModal") private foundingMemberModal!: TemplateRef<any>;
    private modalRef: NgbModalRef | null = null;

    selectedSubscribers: number = 1000;
    selectedPricing: PricingOption = "3-month";
    loading = false;

    limitedOptions: AnimationOptions = {
        animationData: limited,
        loop: false
    };
    private limitedAnimationItem: any;

    onLimitedAnimationCreated(animationItem: any): void {
        this.limitedAnimationItem = animationItem;

        this.limitedAnimationItem.addEventListener('enterFrame', () => {
            const totalFrames = this.limitedAnimationItem.totalFrames;
            const currentFrame = this.limitedAnimationItem.currentFrame;
            const progress = (currentFrame / totalFrames) * 100;
            
            if (progress >= 50) {
                this.limitedAnimationItem.pause();
            }
        });
    }

    subscriberOptions = [
        { value: 1000, label: "1,000 subscribers" },
        { value: 2500, label: "2,500 subscribers" },
        { value: 5000, label: "5,000 subscribers" },
        { value: 10000, label: "10,000 subscribers" },
        { value: 25000, label: "25,000+ subscribers" }
    ];

    pricingPlans: PricingPlan[] = [];

    constructor() {
        this.buildPricingPlans();
    }

    private buildPricingPlans(): void {
        const subscriberKey = this.selectedSubscribers.toString() as "1000" | "2500" | "5000";
        
        const month1 = environment.stripe.prices.v2.month[1][subscriberKey];
        const month3 = environment.stripe.prices.v2.month[3][subscriberKey];
        const year1 = environment.stripe.prices.v2.year[1][subscriberKey];

        this.pricingPlans = [
            {
                id: "1-month",
                label: "1 month",
                originalPrice: month1?.originalPrice || 29,
                currentPrice: month1?.finalPrice || 25,
                pricePerMonth: month1?.finalPrice || 25
            },
            {
                id: "3-month",
                label: "3 months",
                originalPrice: month3?.originalPrice || 29,
                currentPrice: month3?.finalPrice || 22,
                pricePerMonth: month3?.finalPrice || 22,
                isPopular: true
            },
            {
                id: "12-month",
                label: "12 months",
                originalPrice: year1?.originalPrice || 29,
                currentPrice: year1?.finalPrice || 19,
                pricePerMonth: year1?.finalPrice || 19
            }
        ];
    }

    features = [
        "Everything in Free",
        "Email marketing and newsletters",
        "Author swaps and group promos",
        "Multi-retailer links",
        "Unlimited reader magnets",
        "Unlimited ARC campaigns",
        "Custom domain",
        "Unlimited magic automations",
        "Personal support from real humans",
        "Free migration from other tools"
    ];

    ngOnDestroy(): void {
        if (this.modalRef) {
            this.modalRef.close();
        }
    }

    open() {
        posthog.capture("viewed upgrade modal");
        this.selectedPricing = "3-month";
        this.selectedSubscribers = 1000;
        this.buildPricingPlans();
        this.modalRef = this._modalService.open(this.foundingMemberModal, {
            centered: true,
            size: "xl",
            windowClass: "founding-member-modal",
            backdrop: true
        });
        
        this.modalRef.result.finally(() => {
            this.modalRef = null;
        });
    }

    close() {
        if (this.modalRef) {
            this.modalRef.close();
            this.modalRef = null;
        }
    }

    selectPricing(planId: PricingOption) {
        this.selectedPricing = planId;
    }

    onSubscribersChange(): void {
        this.buildPricingPlans();
    }

    selectSubscribers(value: number): void {
        this.selectedSubscribers = value;
        this.onSubscribersChange();
    }

    get selectedSubscriberLabel(): string {
        const option = this.subscriberOptions.find(o => o.value === this.selectedSubscribers);
        return option ? option.label : '1,000 subscribers';
    }

    async upgrade() {
        if (this.loading) return;

        const priceId = this.getPriceId();
        
        if (!priceId) {
            console.error("Price ID not found for selected plan");
            return;
        }

        this.loading = true;
        this._cdr.markForCheck();

        const userEmail = this._authService.user?.email || "";
        const referralId = this.getReferralId();

        // Generate a unique onboarding ID for this checkout
        const onboardingId = `founding-member-${Date.now()}`;
        
        posthog.capture("upgrade_modal_clicked_upgrade");
        await new Promise(resolve => setTimeout(resolve, 1000));

        this._stripeService.startStripeCheckout({
            onboardingId,
            priceId,
            email: userEmail,
            redirectUrl: window.location.pathname,
            referralId
        });
    }

    private getPriceId(): string | null {
        const subscriberKey = this.selectedSubscribers.toString();
        
        let priceConfig: { priceId: string } | undefined;
        
        if (this.selectedPricing === "1-month") {
            priceConfig = environment.stripe.prices.v2.month[1]?.[subscriberKey as "1000" | "2500" | "5000"];
        } else if (this.selectedPricing === "3-month") {
            priceConfig = environment.stripe.prices.v2.month[3]?.[subscriberKey as "1000" | "2500" | "5000"];
        } else if (this.selectedPricing === "12-month") {
            priceConfig = environment.stripe.prices.v2.year[1]?.[subscriberKey as "1000" | "2500" | "5000"];
        } else {
            return null;
        }
        
        return priceConfig?.priceId || null;
    }

    private getReferralId(): string | null {
        try {
            const url = new URL(window.location.href);
            return url.searchParams.get("rs");
        } catch {
            return null;
        }
    }

    get shouldShowDemo(): boolean {
        return this.selectedSubscribers == 25000;
    }
}

